import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { motion } from "framer-motion";
import { Coffee, Leaf, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function Home() {
  const { t } = useI18n();
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-8 min-h-[85vh] max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20">
          <div className="lg:col-span-6 flex flex-col justify-center order-2 lg:order-1">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="label-eyebrow text-[hsl(var(--sk-primary))] mb-6">
              {t("home.eyebrow")}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter whitespace-pre-line">
              {t("home.title")}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 text-lg text-[hsl(var(--sk-muted-fg))] max-w-lg leading-relaxed">
              {t("home.subtitle")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-4">
              <Button asChild size="lg" className="rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90 text-white px-8">
                <Link to="/menu" data-testid="hero-menu-btn">{t("home.cta_menu")} <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-[hsl(var(--sk-primary))] text-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))] hover:text-white px-8">
                <Link to="/shop" data-testid="hero-shop-btn">{t("home.cta_shop")}</Link>
              </Button>
            </motion.div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 grid grid-cols-6 grid-rows-6 gap-3 min-h-[400px] lg:min-h-full">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }}
              className="col-span-4 row-span-6 rounded-2xl overflow-hidden bg-[hsl(var(--sk-muted))]">
              <img src="https://images.pexels.com/photos/31287156/pexels-photo-31287156.jpeg" alt="" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
              className="col-span-2 row-span-3 rounded-2xl overflow-hidden">
              <img src="https://images.pexels.com/photos/4829080/pexels-photo-4829080.jpeg" alt="" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35 }}
              className="col-span-2 row-span-3 rounded-2xl bg-[hsl(var(--sk-primary))] text-white p-6 flex flex-col justify-between">
              <Coffee size={32} weight="duotone" />
              <div>
                <p className="font-display text-3xl leading-none">4</p>
                <p className="label-eyebrow mt-1 text-white/70">Single-origin</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="label-eyebrow text-[hsl(var(--sk-accent))]">{t("home.section1_eyebrow")}</p>
        </div>
        <div className="lg:col-span-8">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-2xl">
            {t("home.section1_title")}
          </h2>
          <p className="mt-8 text-lg text-[hsl(var(--sk-muted-fg))] max-w-2xl leading-relaxed">
            {t("home.section1_body")}
          </p>
          <div className="mt-10 flex flex-wrap gap-8">
            <div className="flex items-center gap-3">
              <Leaf size={24} weight="duotone" className="text-[hsl(var(--sk-accent))]" />
              <span className="text-sm">Direct trade</span>
            </div>
            <div className="flex items-center gap-3">
              <Coffee size={24} weight="duotone" className="text-[hsl(var(--sk-accent))]" />
              <span className="text-sm">Small batch roast</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[hsl(var(--sk-secondary))] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <p className="label-eyebrow text-[hsl(var(--sk-primary))] mb-3">Reserve</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-xl">Rayakan momen di ruang kami.</h2>
          </div>
          <Button asChild size="lg" className="rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90 text-white px-8">
            <Link to="/events" data-testid="home-events-btn">{t("events.reserve")} <ArrowRight size={16} className="ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
